import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Dayjs } from "dayjs"

import { useLazyScheduleMessageForDevicesQuery } from "store/api/contractorsApi"
import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useGetDevicesQuery } from "store/api/devicesApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { showPerformanceTestConfirmModal } from "store/modalSlise"
import { setModalContent } from "store/tempDataSlise"
import { UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import { convertDevicesToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import TableWrapper from "components/table/TableWrapper"

import { dummyDevices, dummyMeta } from "../MonitoringPage/dummyMonitoringData"
import { MessageCenterHeader } from "./MessageCenterHeader"
import { messageCenterHeaderCells } from "./MessageCenterHeaderCells"

interface MessageCenterProps {
  hideUnavailableModal?: boolean
}

export const MessageCenter = ({ hideUnavailableModal = false }: MessageCenterProps) => {
  const { urlQueryParams } = useUrlQueryParams()
  const { data, isFetching } = useGetDevicesQuery(
    { ...urlQueryParams, phisical_status: "installed" },
    {
      pollingInterval: 30000,
      skipPollingIfUnfocused: true,
    },
  )
  const dispatch = useAppDispatch()

  const [triggerScheduleMessageForDevices, { isFetching: isScheduleMessageForDevicesFetching }] =
    useLazyScheduleMessageForDevicesQuery()
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const [templateID, setTemplateID] = useState<undefined | UITypes.Option[]>(undefined)
  const [deviceIDs, setDeviceIDs] = useState<number[]>([])
  const [isHeaderChecked, setHeaderChecked] = useState(false)
  const [isFirstRender, setFirstRender] = useState(true)
  const hasMessagesCenterAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.MessageCenter)
    : false

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(messageCenterHeaderCells, columnsData?.data?.config)
        : messageCenterHeaderCells,
    [columnsData],
  )

  const toggleAllDevices = () => {
    if (isHeaderChecked) {
      setDeviceIDs([])
      setHeaderChecked(false)
    } else {
      const result = data?.data?.map(item => item?.id)
      if (result) {
        setDeviceIDs(result)
      }
      setHeaderChecked(true)
    }
  }

  const onSelectDevice = (id?: number) => {
    if (deviceIDs?.some(item => item === id)) {
      setDeviceIDs(deviceIDs?.filter(item => item !== id))
    } else {
      setDeviceIDs([...deviceIDs, ...[id]] as number[])
    }
  }

  const onSendMessage = async () => {
    // if (!templateID) return
    // const data = {
    //   body: {
    //     device_ids: deviceIDs,
    //   },
    // }
    // try {
    //   const res = await triggerScheduleMessageForDevices(data)
    //   const error = getErrorMessage(res?.error)
    //   if (error) {
    //     toast.error(error)
    //   } else {
    //     toast.success(
    //       "The messages will be
    // sent to the selected group during 1 hour. Navigate to History page to check the status of the sent messages.",
    //     )
    //     setDeviceIDs([])
    //     setTemplateID(undefined)
    //     setHeaderChecked(false)
    //   }
    // } catch (error) {
    //   console.error(error)
    // }
  }

  const handleSendScheduledMessage = async (selectedDateTime: Dayjs) => {
    if (!templateID) return

    const data = {
      device_ids: deviceIDs,
      scheduled_delivery_timestamp: selectedDateTime.toISOString(),
      message_template_id: templateID[0]?.value as number,
    }

    try {
      const res = await triggerScheduleMessageForDevices(data)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        setDeviceIDs([])
        setTemplateID(undefined)
        setHeaderChecked(false)

        dispatch(
          setModalContent({
            title: "Successfully scheduled",
            content: (
              <>
                Message will be sent to the customer(s) on <br /> {selectedDateTime.format("MMMM DD, YYYY hh:mm A")}
              </>
            ),
          }),
        )
        dispatch(showPerformanceTestConfirmModal(true))
      }
    } catch (error) {
      console.error(error)
    }
  }

  const meta = hasMessagesCenterAccess ? data?.meta : dummyMeta

  const devices = useMemo(() => {
    if (!hasMessagesCenterAccess) {
      return dummyDevices
    }

    return data?.data || []
  }, [data, hasMessagesCenterAccess])

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    triggerGetTableColumns({ list_name: "contractor_messages_list" })
  }, [isFirstRender])

  return (
    <>
      <ContentWrapper title="Message Center">
        <MessageCenterHeader
          templateID={templateID}
          setTemplateID={setTemplateID}
          deviceIDs={deviceIDs}
          onSendMessage={onSendMessage}
          onSendScheduledMessage={handleSendScheduledMessage}
          isGenerateMessagesFetching={isScheduleMessageForDevicesFetching}
          tableHeaderCells={tableHeaderCells}
          hasMessagesCenterAccess={hasMessagesCenterAccess}
        />
        <TableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={!!columnsData}
          data={convertDevicesToTableRows(devices)}
          isLoading={isFetching}
          onCheckboxChange={onSelectDevice}
          onHeaderCheckboxChange={toggleAllDevices}
          currentItemIDs={deviceIDs}
          isHeaderChecked={isHeaderChecked}
        />
        <Pagination meta={meta} />
      </ContentWrapper>
      {!hasMessagesCenterAccess && (
        <SubscriptionUpgradeModal
          page={SubscriptionPrivilege.MessageCenter}
          hideUnavailableModal={hideUnavailableModal}
        />
      )}
    </>
  )
}
